/**
 * A school's own look: its colour on the top bar and its logo as the home-screen icon.
 * Called with the school's settings when we know which school this is; null restores the defaults.
 */
const DEFAULT = '#1B2A4A';

function luminance(hex: string) {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function applyBrand(color: string | null | undefined) {
  const root = document.documentElement.style;
  const c = color && /^#[0-9a-f]{6}$/i.test(color) ? color : null;
  if (!c) {
    for (const v of ['--brand', '--on-brand', '--on-brand-2', '--brand-stripe']) root.removeProperty(v);
  } else {
    // Text on the school's colour: white on dark colours, navy on light ones
    const light = luminance(c) > 0.4;
    root.setProperty('--brand', c);
    root.setProperty('--on-brand', light ? '#1b2a4a' : '#ffffff');
    root.setProperty('--on-brand-2', light ? 'rgb(27 42 74 / 0.75)' : 'rgb(255 255 255 / 0.78)');
    root.setProperty('--brand-stripe', c);
  }
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', c ?? DEFAULT);
}

/** iPhone reads the home-screen icon and title from the page when "Add to Home Screen" is tapped */
export function applyAppIcon(logoUrl: string | null | undefined, title: string | null | undefined) {
  const icon = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  if (icon) icon.href = logoUrl || '/icons/apple-touch-icon.png';
  const t = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (t && title) t.setAttribute('content', title);
}

/** Squares an uploaded logo onto a white 512×512 PNG, so it works as an app icon everywhere */
export async function squareLogo(file: File): Promise<File> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const size = 512;
    const pad = 40;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
    const scale = Math.min((size - pad * 2) / img.width, (size - pad * 2) / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    return blob ? new File([blob], 'logo.png', { type: 'image/png' }) : file;
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}
